import GraphNodeModel from "app/components/flow/designer/graphNodes/graphNodeModel";
import componentService, { componentID, ComponentKeys } from "core/services/componentService/component-service";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import cqTransformationProvider from "app/components/common/editor/meta-object-editors/cqTransformations/transformationProvider";
import utils from "core/utils";
import { Node } from "reactflow";
import { RefObject } from "react";
import dialog from "app/components/common/dialogs/dialogWindow";
import App from "app";
import { closeAllSidebarsAction } from "../../../../../stores/redux/features/flowdesigner/flowdesigner.slice";
import { SidebarWrapperRef } from "./sidebar-wrapper";

/**
 * Component metadata extracted from component service
 */
interface ComponentMetadata {
    metadata: any;
    adapter: string | null;
    parser: string | null;
}

/**
 * Get component metadata including adapter and parser from component service
 */
export const getComponentMetadata = (componentId: componentID | undefined): ComponentMetadata => {
    if (!componentId) {
        return { metadata: null, adapter: null, parser: null };
    }

    const metadata = componentService.getMetadata(componentId);
    const adapter = metadata?.[ComponentKeys.Adapter] || null;
    const parser = metadata?.[ComponentKeys.Parser] || null;

    return { metadata, adapter, parser };
};

/**
 * Create auto properties for adapter and parser
 * These are prefilled in the form based on component metadata
 */
export const createAutoProperties = (
    adapter: string | null,
    parser: string | null,
    componentId: componentID | undefined
): Record<string, any> => {
    const autoProperties: Record<string, any> = {};

    // Get additional properties from component service (like isEventTable for EventTable)
    // This matches the old flow designer's predefinedComponentProcessor.js behavior
    if (componentId) {
        const prefillProperties = componentService.getPrefillProperties(componentId);
        Object.assign(autoProperties, prefillProperties);
    }

    // Override adapter if explicitly provided (for SOURCE/TARGET components)
    if (adapter) {
        // Adapter must be an object with a 'handler' property
        // The handler must be prefixed with Global.PROPERTYTEMPLATE.
        autoProperties.adapter = {
            handler: MetaObjectConverter.getGlobalId(metaStoreService.entities.PROPERTYTEMPLATE, adapter)
        };

        // Include adapter properties (e.g., DatabaseProviderType for DatabaseReader)
        if (componentId) {
            const adapterProperties = componentService.getAdapterProperties(componentId);
            if (adapterProperties && Object.keys(adapterProperties).length > 0) {
                autoProperties.adapter.properties = adapterProperties;
            }
        }
    }

    // Override parser if explicitly provided (for SOURCE components)
    if (parser) {
        // Parser handler must also be prefixed with Global.PROPERTYTEMPLATE.
        autoProperties.parser = {
            handler: MetaObjectConverter.getGlobalId(metaStoreService.entities.PROPERTYTEMPLATE, parser)
        };
    }

    // Add namespace prefix to adapter/parser handlers if they came from prefillProperties
    // and don't have the prefix yet (matches predefinedComponentProcessor.js)
    if (autoProperties.adapter?.handler && typeof autoProperties.adapter.handler === "string") {
        if (!autoProperties.adapter.handler.startsWith("Global.PROPERTYTEMPLATE.")) {
            autoProperties.adapter.handler = MetaObjectConverter.getGlobalId(
                metaStoreService.entities.PROPERTYTEMPLATE,
                autoProperties.adapter.handler
            );
        }
    }

    if (autoProperties.parser?.handler && typeof autoProperties.parser.handler === "string") {
        if (!autoProperties.parser.handler.startsWith("Global.PROPERTYTEMPLATE.")) {
            autoProperties.parser.handler = MetaObjectConverter.getGlobalId(
                metaStoreService.entities.PROPERTYTEMPLATE,
                autoProperties.parser.handler
            );
        }
    }

    // Handle CQ transformations (like Euclid, Foreseer, Sherlock, etc.)
    if (componentId) {
        const metadata = componentService.getMetadata(componentId);
        const componentType = metadata?.[ComponentKeys.Type];
        const humanReadableName = metadata?.[ComponentKeys.HumanReadableName];

        if (componentType === utils.ENTITIES.CQ && humanReadableName) {
            // Check if this is a predefined CQ transformation
            const cqTransformation = cqTransformationProvider.getTransformationByName(humanReadableName);
            if (cqTransformation) {
                // Set up uiconfig for CQ transformations (similar to cqProcessor.js)
                autoProperties.uiconfig = {
                    subType: humanReadableName,
                    config: {}
                };
            }
        }
    }

    return autoProperties;
};

/**
 * Create meta object from React Flow node data
 */
export const createMetaObject = (selectedNode: Node, app: any, autoProperties: Record<string, any>): any => {
    const metaObject: any = {
        type: selectedNode.data?.type || selectedNode.type,
        name: selectedNode.data?.label || selectedNode.data?.name || "New Component",
        ...(selectedNode.data?.metaObject || {}),
        nsName: app.nsName // Set namespace for name validation (must be after spread to avoid being overridden)
    };

    // Set autoProperties to prefill the adapter/parser in the form.
    // Merge in any connection properties (inputStream, stream, inStream, select, etc.) that were
    // set when creating nodes via drag-and-drop on an existing node or via the "add new node" button,
    // so they are not overwritten by the adapter/parser defaults from createAutoProperties.
    if (Object.keys(autoProperties).length > 0) {
        const nodeConnectionProperties = (selectedNode.data?.metaObject?.autoProperties as Record<string, any>) || {};
        metaObject.autoProperties = { ...autoProperties, ...nodeConnectionProperties };
    }

    return metaObject;
};

/**
 * Create a Backbone GraphNodeModel from React Flow node
 */
export const createGraphNodeModel = (
    selectedNode: Node,
    metaObject: any,
    componentMetadata: any,
    flowId: string,
    app: any
): GraphNodeModel => {
    return new GraphNodeModel({
        id: selectedNode.data?.metaObjectId || "temp-node",
        metaObject: metaObject,
        predefinedComponentName: componentMetadata?.[ComponentKeys.HumanReadableName],
        targets: selectedNode.data?.targets || [],
        flowId: flowId,
        applicationId: app.id
    });
};

/**
 * Event handlers for sidebar
 */
export interface SidebarEventHandlers {
    onSidebarSave?: (data: any) => void;
    onSidebarCancel?: () => void;
    onDelete?: () => void;
    onPreviewData?: (model: any) => void;
    onClearAndReload?: () => void;
}

/**
 * Attach event listeners to sidebar view
 */
export const attachSidebarEventListeners = (sidebarView: any, handlers: SidebarEventHandlers): void => {
    const { onSidebarSave, onSidebarCancel, onDelete, onPreviewData, onClearAndReload } = handlers;

    if (onSidebarSave) {
        sidebarView.on("sidebar-save", onSidebarSave);
    }
    if (onSidebarCancel) {
        sidebarView.on("sidebar-cancel", onSidebarCancel);
    }
    if (onDelete) {
        sidebarView.on("delete", onDelete);
    }
    if (onPreviewData) {
        sidebarView.on("preview-data", onPreviewData);
    }
    if (onClearAndReload) {
        sidebarView.on("clear-and-reload", onClearAndReload);
    }
};

/**
 * Render sidebar view into container and trigger lifecycle methods
 */
export const renderSidebarView = (sidebarView: any, container: HTMLElement): void => {
    sidebarView.render();
    container.replaceChildren(sidebarView.el);

    // Trigger onShow lifecycle
    if (sidebarView.triggerMethod) {
        sidebarView.triggerMethod("show");
    }
};

/**
 * Cleanup sidebar view
 */
export const cleanupSidebarView = (sidebarView: any): void => {
    if (sidebarView) {
        sidebarView.destroy();
    }
};

/**
 * Returns true if either the component form or app settings sidebar has unsaved changes
 */
export const sidebarHasUnsavedChanges = (
    sidebarRef: RefObject<SidebarWrapperRef>,
    appSettingsRef: RefObject<any>
): boolean => {
    const componentFormDirty = sidebarRef.current && sidebarRef.current.isDirty();
    const appSettingsDirty = appSettingsRef.current && appSettingsRef.current.isDirty();
    return !!(componentFormDirty || appSettingsDirty);
};

/**
 * Prompts the user to confirm navigation away from unsaved changes.
 * Closes all sidebars if the user confirms.
 */
export const promptIfUnsavedChanged = async (
    sidebarRef: RefObject<SidebarWrapperRef>,
    appSettingsRef: RefObject<any>,
    dispatch: (action: any) => void,
    appName: string | undefined,
    flowName: string | undefined
): Promise<boolean> => {
    if (!sidebarHasUnsavedChanges(sidebarRef, appSettingsRef)) return true;
    const message = "You have unsaved changes. Do you want to proceed?";
    const result = await dialog.confirm(message, App.Dashboards.ModalManager);
    if (result) dispatch(closeAllSidebarsAction({ appName, flowName }));
    return result;
};

/**
 * Alerts the user that unsaved changes must be resolved before a status change.
 * Returns false (blocking the action) if there are unsaved changes.
 */
export const promptIfUnsavedChangedForStatus = async (
    sidebarRef: RefObject<SidebarWrapperRef>,
    appSettingsRef: RefObject<any>
): Promise<boolean> => {
    if (!sidebarHasUnsavedChanges(sidebarRef, appSettingsRef)) return true;
    const message = "There are unsaved changes. Please save or cancel changes before proceeding.";
    await dialog.alert(message, App.Dashboards.ModalManager);
    return false;
};
